/**
 * Pure mapper: the EXISTING trade-review response (`RsiTradeReview`) -> Lightweight Charts
 * series for the admin TEK_RSI_MOMENTUM intraday chart. No React, no network, no canvas.
 *
 * Nothing is calculated or invented here:
 *  - candles are the stored NIFTY 5-minute candles exactly as the API returns them;
 *  - markers are the stored entry / signal / exit / partial events, placed on the candle slot
 *    the API itself assigned (`index`); signals are never re-derived on the client.
 *
 * TIME. The API's `start_ist` / `time_ist` are ISO strings WITH an explicit `+05:30` offset
 * (backend `trade_review.iso_ist`, built from IST wall-clock). They are converted to UTC epoch
 * SECONDS here and only formatted back to IST for display (see `zonedTime.ts`).
 *
 * Gaps stay gaps: a missing / invalid candle is left blank (counted, never interpolated), and
 * an event that falls on such a slot is reported in `unplacedEvents` instead of being moved.
 */
import {
  VOLUME_DOWN_COLOR,
  VOLUME_UP_COLOR,
  type CandlePoint,
  type ChartMarker,
  type VolumePoint,
} from "@/components/charts/candlestickChartData";
import { formatZonedTick, isoToEpochSeconds } from "@/components/charts/zonedTime";
import type { RsiTradeReview } from "@/types";

/** The strategy's candle size (backend `SLOT_MINUTES`). Used only to bucket an event time into its slot. */
export const SLOT_SECONDS = 5 * 60;

/** Context kept either side of the trade in the default "Focus trade" view (12 candles, as the Chart.js review does). */
export const FOCUS_PAD_SLOTS = 12;
/** Start of the session's last 5-minute slot (backend SESSION_CLOSE 15:30 minus one slot). */
export const LAST_SLOT_START_IST = "15:25";

export const MARKER_COLORS = {
  buy: "#2E7D32",
  sell: "#C62828",
  signal: "#455A64",
  blocked: "#9E9E9E",
  exit: "#212121",
  partial: "#66BB6A",
} as const;

export interface TradeReviewLwData {
  candles: CandlePoint[];
  /** Empty when the session has no usable volume (NIFTY spot normally reports 0). */
  volume: VolumePoint[];
  hasVolume: boolean;
  markers: ChartMarker[];
  /** Slots that are missing / invalid / unparseable and therefore left blank. */
  skippedCandles: number;
  /** Recorded events that could not be drawn (their candle is missing/invalid or outside the session). */
  unplacedEvents: string[];
  /** Every recorded event as a text line (IST time, label, reason/price), in time order. Markers stay short; this carries the detail. */
  eventLines: string[];
  /** Default zoom: this trade's events +/- FOCUS_PAD_SLOTS candles, in epoch seconds; absent when nothing was drawn. */
  focusRange?: { from: number; to: number };
  /** "HH:MM" IST of the last candle slot when the API's session ends before 15:25 (in-progress / truncated); else null. */
  sessionEndsAtIst: string | null;
}

interface Slot {
  start: number;
  /** True when a valid candle is drawn at this slot. */
  usable: boolean;
}

const finitePositive = (v: number | null): v is number => v !== null && Number.isFinite(v) && v > 0;

export function buildTradeReviewLwData(review: RsiTradeReview | null | undefined): TradeReviewLwData {
  const empty: TradeReviewLwData = {
    candles: [], volume: [], hasVolume: false, markers: [], skippedCandles: 0, unplacedEvents: [], eventLines: [], sessionEndsAtIst: null,
  };
  if (!review) return empty;
  const tradeDate = review.summary.trade_date;

  // ---- candles (by API slot index, so events can be resolved even for blank slots)
  const slots = new Map<number, Slot>();
  const drawn = new Map<number, { candle: CandlePoint; volume: number }>();
  let skippedCandles = 0;
  for (const c of review.candles ?? []) {
    const start = isoToEpochSeconds(c.start_ist) ?? isoToEpochSeconds(`${tradeDate}T${c.time}:00`);
    if (start === null) {
      skippedCandles += 1;
      continue;
    }
    const { open, high, low, close } = c;
    const ok =
      !c.missing &&
      c.valid !== false &&
      finitePositive(open) &&
      finitePositive(high) &&
      finitePositive(low) &&
      finitePositive(close) &&
      high >= low;
    slots.set(c.index, { start, usable: ok });
    if (!ok) {
      skippedCandles += 1;
      continue;
    }
    // Same epoch twice (never expected): the later row wins, like the daily mapper.
    drawn.set(start, {
      candle: { time: start, open, high, low, close },
      volume: c.volume !== null && Number.isFinite(c.volume) && c.volume > 0 ? c.volume : 0,
    });
  }
  const times = Array.from(drawn.keys()).sort((a, b) => a - b);
  const candles = times.map((t) => drawn.get(t)!.candle);
  const hasVolume = times.some((t) => drawn.get(t)!.volume > 0);
  const volume: VolumePoint[] = hasVolume
    ? times.map((t) => {
        const { candle, volume: v } = drawn.get(t)!;
        return { time: t, value: v, color: candle.close >= candle.open ? VOLUME_UP_COLOR : VOLUME_DOWN_COLOR };
      })
    : [];

  // ---- event -> slot resolution
  const slotByEpoch = (epoch: number): Slot | null => {
    for (const s of slots.values()) if (epoch >= s.start && epoch < s.start + SLOT_SECONDS) return s;
    return null;
  };
  /** The API's own slot `index` wins; otherwise bucket the recorded time (ISO, else HH:MM on the trade date). */
  const resolve = (index: number | null | undefined, iso?: string | null, hhmm?: string | null): Slot | null => {
    if (index !== null && index !== undefined) return slots.get(index) ?? null;
    const epoch = isoToEpochSeconds(iso) ?? (hhmm ? isoToEpochSeconds(`${tradeDate}T${hhmm}:00`) : null);
    return epoch === null ? null : slotByEpoch(epoch);
  };

  const markers: ChartMarker[] = [];
  const unplacedEvents: string[] = [];
  const events: Array<{ at: number; line: string }> = [];
  const price = (v: number | null | undefined) =>
    v === null || v === undefined ? "" : ` at ${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  /**
   * Draws one recorded event (a SHORT marker) when its candle is usable, and ALWAYS records it as a
   * text line, so detail (reason, price, quantity) is never lost to marker clutter or clipping.
   */
  const place = (
    label: string,
    when: { iso?: string | null; hhmm?: string | null },
    slot: Slot | null,
    marker: Omit<ChartMarker, "time">,
    detail: string
  ) => {
    const epoch = isoToEpochSeconds(when.iso) ?? (when.hhmm ? isoToEpochSeconds(`${tradeDate}T${when.hhmm}:00`) : null);
    const whenText = epoch !== null && when.iso ? formatZonedTick(epoch, 4) : when.hhmm ?? "time n/a";
    const drawnOk = Boolean(slot && slot.usable);
    if (slot && slot.usable) markers.push({ ...marker, time: slot.start });
    else unplacedEvents.push(`${label} ${when.hhmm ?? whenText}`.trim());
    events.push({
      at: epoch ?? slot?.start ?? Number.POSITIVE_INFINITY,
      line: `${whenText} IST  ${detail}${drawnOk ? "" : " (not drawn: its candle is missing or outside the session)"}`,
    });
  };

  const m = review.markers;
  const isLong = review.summary.side === "LONG";

  // this trade's signal bar (the recorded signal that produced the entry)
  const thisSignal = (m.signals ?? []).find((s) => s.is_this_trade);
  const signalIndex = m.entry.signal_index ?? thisSignal?.index ?? null;
  if (signalIndex !== null) {
    const sigTime = thisSignal?.time ?? null;
    place("Signal", { hhmm: sigTime }, resolve(signalIndex, null, sigTime), {
      position: isLong ? "belowBar" : "aboveBar",
      shape: "circle",
      color: MARKER_COLORS.signal,
      text: "Signal",
      id: "signal",
    }, "Signal bar that led to this entry");
  }
  // every other recorded signal for the day (blocked / executed for another trade)
  (m.signals ?? []).forEach((s, i) => {
    if (s.is_this_trade || (signalIndex !== null && s.index === signalIndex && s.status !== "BLOCKED")) return;
    const blocked = s.status === "BLOCKED";
    place(blocked ? "Blocked signal" : "Signal", { hhmm: s.time }, resolve(s.index, null, s.time), {
      position: "aboveBar",
      shape: "circle",
      color: blocked ? MARKER_COLORS.blocked : MARKER_COLORS.signal,
      text: blocked ? "Blocked" : "Signal",
      id: `signal-${i}`,
    }, blocked ? `Blocked ${s.direction} signal: ${s.reason ?? "reason not recorded"}` : `${s.direction} signal (${s.status.toLowerCase()})`);
  });

  place(m.entry.label, { iso: m.entry.time_ist, hhmm: m.entry.time }, resolve(m.entry.index, m.entry.time_ist, m.entry.time), {
    position: isLong ? "belowBar" : "aboveBar",
    shape: isLong ? "arrowUp" : "arrowDown",
    color: isLong ? MARKER_COLORS.buy : MARKER_COLORS.sell,
    text: m.entry.label,
    id: "entry",
  }, `${m.entry.label} entry${price(m.entry.price)}`);

  if (m.exit) {
    place("Exit", { iso: m.exit.time_ist, hhmm: m.exit.time }, resolve(m.exit.index, m.exit.time_ist, m.exit.time), {
      position: isLong ? "aboveBar" : "belowBar",
      shape: "square",
      color: MARKER_COLORS.exit,
      text: "EXIT",
      id: "exit",
    }, `EXIT ${m.exit.reason ?? "(reason not recorded)"}${price(m.exit.price)}`);
  }

  (m.partials ?? []).forEach((p, i) => {
    place("Partial", { hhmm: p.time }, resolve(p.index, null, p.time), {
      position: "inBar",
      shape: "circle",
      color: MARKER_COLORS.partial,
      text: "Partial",
      id: `partial-${i}`,
    }, `Partial booking, quantity ${p.quantity}`);
  });

  // The library expects markers in time order; ties keep their insertion order (stable sort).
  markers.sort((a, b) => Number(a.time) - Number(b.time));
  events.sort((a, b) => a.at - b.at);

  // ---- default zoom: this TRADE's own events (not other blocked signals) +/- context, clamped to the data
  let focusRange: TradeReviewLwData["focusRange"];
  const own = markers.filter((mk) => mk.id === "signal" || mk.id === "entry" || mk.id === "exit" || mk.id?.startsWith("partial-"));
  if (own.length > 0 && candles.length > 1) {
    const firstT = candles[0].time as number;
    const lastT = candles[candles.length - 1].time as number;
    const exitDrawn = own.some((mk) => mk.id === "exit");
    const lo = Math.min(...own.map((mk) => Number(mk.time)));
    const hi = exitDrawn || m.exit ? Math.max(...own.map((mk) => Number(mk.time))) : lastT; // an open trade runs to the last candle
    const from = Math.max(firstT, lo - FOCUS_PAD_SLOTS * SLOT_SECONDS);
    const to = Math.min(lastT, hi + FOCUS_PAD_SLOTS * SLOT_SECONDS);
    if (from < to) focusRange = { from, to };
  }

  // ---- session completeness, judged from the API's own slot grid (so one missing last candle is not "partial")
  let sessionEndsAtIst: string | null = null;
  if (slots.size > 0) {
    const lastSlotStart = Math.max(...Array.from(slots.values(), (sl) => sl.start));
    const sessionLast = isoToEpochSeconds(`${tradeDate}T${LAST_SLOT_START_IST}:00`);
    if (sessionLast !== null && lastSlotStart < sessionLast) sessionEndsAtIst = formatZonedTick(lastSlotStart, 3);
  }

  return {
    candles, volume, hasVolume, markers, skippedCandles, unplacedEvents,
    eventLines: events.map((e) => e.line), focusRange, sessionEndsAtIst,
  };
}
